import { useEffect, useState } from 'react'
import { Mail, Download, Users, MessageSquare } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { adminApi, WaitlistEntry } from '../../utils/api'

export default function AdminWaitlistPage() {
  const { token } = useAuth()
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadWaitlist() {
      if (!token) return

      try {
        const data = await adminApi.getWaitlist(token)
        setEntries(data.items)
      } catch (err) {
        console.error('[AdminWaitlist] Failed to load waitlist:', err)
        setError('Failed to load waitlist')
      } finally {
        setLoading(false)
      }
    }

    loadWaitlist()
  }, [token])

  const exportToCSV = () => {
    const headers = 'Email,Tool Interests'
    const rows = entries.map(e =>
      `"${e.email}","${(e.tool_interests || '').replace(/"/g, '""')}"`
    )
    const csv = [headers, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const copyAllEmails = () => {
    const emails = entries.map(e => e.email).join('\n')
    navigator.clipboard.writeText(emails)
  }

  const entriesWithInterests = entries.filter(e => e.tool_interests)

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waitlist</h1>
          <p className="text-gray-500 mt-1">
            Users who signed up for early access
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={copyAllEmails}
            disabled={entries.length === 0}
            className="btn btn-secondary text-sm flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Copy All Emails
          </button>
          <button
            onClick={exportToCSV}
            disabled={entries.length === 0}
            className="btn btn-primary text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Signups</p>
              <p className="text-3xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block w-12 h-8 bg-gray-200 rounded animate-pulse" />
                ) : (
                  entries.length.toLocaleString()
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">With Skill Interests</p>
              <p className="text-3xl font-bold text-gray-900">
                {loading ? (
                  <span className="inline-block w-12 h-8 bg-gray-200 rounded animate-pulse" />
                ) : (
                  entriesWithInterests.length.toLocaleString()
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Entries List */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Signups</h2>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">{error}</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No waitlist signups yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map((entry, index) => (
              <div
                key={entry.email}
                className="px-6 py-4 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-gray-400 w-8 pt-0.5">{index + 1}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">{entry.email}</span>
                      </div>
                      {entry.tool_interests && (
                        <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                          <span className="font-medium text-gray-700">Interests: </span>
                          {entry.tool_interests}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(entry.email)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
